import { __awaiter, __generator } from "tslib";
import { SchemaActionType, KeyGenerator, TransactionMode, DataActionType, } from '@tencent/memoire-core';
import { IncorrectTransactionModeError } from '../error/errors';
var MigrationActionExecutor = /** @class */ (function () {
    function MigrationActionExecutor(aspects) {
        this.schemaActionHandlerMap = new Map();
        this.dataActionHandlerMap = new Map();
        this.schemaActionHandlerMap.set(SchemaActionType.CreateCollection, this.performCreateCollectionAction.bind(this));
        this.schemaActionHandlerMap.set(SchemaActionType.DropCollection, this.performDropCollectionAction.bind(this));
        this.schemaActionHandlerMap.set(SchemaActionType.CreateIndex, this.performCreateIndexAction.bind(this));
        this.schemaActionHandlerMap.set(SchemaActionType.DropIndex, this.performDropIndexAction.bind(this));
        this.dataActionHandlerMap.set(DataActionType.DropProperty, this.performDropPropertyAction.bind(this));
        this.dataActionHandlerMap.set(DataActionType.ClearData, this.performClearDataAction.bind(this));
        this.dataActionHandlerMap.set(DataActionType.ReplaceData, this.performReplaceDataAction.bind(this));
        // apply aspect
        aspects.executeDataAction.patch(this, 'executeDataAction');
        aspects.executeSchemaAction.patch(this, 'executeSchemaAction');
    }
    // executeAction() takes an migration action (schema or data migration)
    // it will whether upgrade the database schema or modify the data in db,
    // see: packages/memoire-core/migration/action.ts
    MigrationActionExecutor.prototype.executeSchemaAction = function (db, transaction, action) {
        var handler = this.schemaActionHandlerMap.get(action.type);
        if (!handler) {
            return;
        }
        if (transaction.mode !== TransactionMode.VERSION_CHANGE) {
            throw new IncorrectTransactionModeError(action.type);
        }
        return handler(db, transaction, action);
    };
    MigrationActionExecutor.prototype.executeDataAction = function (db, transaction, action) {
        return __awaiter(this, void 0, void 0, function () {
            var handler;
            return __generator(this, function (_a) {
                handler = this.dataActionHandlerMap.get(action.type);
                if (!handler) {
                    return [2 /*return*/];
                }
                return [2 /*return*/, handler(db, transaction, action)];
            });
        });
    };
    MigrationActionExecutor.prototype.performCreateIndexAction = function (db, transaction, action) {
        var collection = transaction.table(action.collection);
        collection.createIndex(action.name, action.attributes.path, {
            unique: action.attributes.unique,
        });
    };
    MigrationActionExecutor.prototype.performDropIndexAction = function (db, transaction, action) {
        var collection = transaction.table(action.collection);
        collection.deleteIndex(action.name);
    };
    MigrationActionExecutor.prototype.performDropCollectionAction = function (db, transaction, action) {
        db.deleteTable(action.name);
    };
    MigrationActionExecutor.prototype.performCreateCollectionAction = function (db, transaction, action) {
        var _a, _b;
        db.createTable(action.name, {
            keyPath: (_a = action.attributes.key) === null || _a === void 0 ? void 0 : _a.path,
            autoIncrement: ((_b = action.attributes.key) === null || _b === void 0 ? void 0 : _b.generator) === KeyGenerator.AutoIncrement,
        });
    };
    MigrationActionExecutor.prototype.performClearDataAction = function (db, transaction, action) {
        return __awaiter(this, void 0, void 0, function () {
            var collection, cursor;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        collection = transaction.table(action.collection);
                        if (!!action.filter) return [3 /*break*/, 2];
                        return [4 /*yield*/, collection.clear()];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                    case 2: return [4 /*yield*/, collection.openCursor()];
                    case 3:
                        cursor = _a.sent();
                        _a.label = 4;
                    case 4:
                        if (!cursor) return [3 /*break*/, 8];
                        if (!action.filter(cursor.value())) return [3 /*break*/, 6];
                        return [4 /*yield*/, cursor.delete()];
                    case 5:
                        _a.sent();
                        _a.label = 6;
                    case 6: return [4 /*yield*/, cursor.next()];
                    case 7:
                        cursor = _a.sent();
                        return [3 /*break*/, 4];
                    case 8: return [2 /*return*/];
                }
            });
        });
    };
    MigrationActionExecutor.prototype.performDropPropertyAction = function (db, transaction, action) {
        return __awaiter(this, void 0, void 0, function () {
            var collection, cursor, data;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        collection = transaction.table(action.collection);
                        return [4 /*yield*/, collection.openCursor()];
                    case 1:
                        cursor = _a.sent();
                        _a.label = 2;
                    case 2:
                        if (!cursor) return [3 /*break*/, 5];
                        data = cursor.value();
                        data[action.path] = undefined;
                        return [4 /*yield*/, cursor.update(data)];
                    case 3:
                        _a.sent();
                        return [4 /*yield*/, cursor.next()];
                    case 4:
                        cursor = _a.sent();
                        return [3 /*break*/, 2];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    MigrationActionExecutor.prototype.performReplaceDataAction = function (db, transaction, action) {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var collection, cursor, data;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        collection = transaction.table(action.collection);
                        return [4 /*yield*/, collection.openCursor()];
                    case 1:
                        cursor = _b.sent();
                        _b.label = 2;
                    case 2:
                        if (!cursor) return [3 /*break*/, 6];
                        data = cursor.value();
                        if (!(!action.filter || ((_a = action.filter) === null || _a === void 0 ? void 0 : _a.call(this, data)))) return [3 /*break*/, 4];
                        return [4 /*yield*/, cursor.update(action.replacer(data))];
                    case 3:
                        _b.sent();
                        _b.label = 4;
                    case 4: return [4 /*yield*/, cursor.next()];
                    case 5:
                        cursor = _b.sent();
                        return [3 /*break*/, 2];
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    return MigrationActionExecutor;
}());
export { MigrationActionExecutor };
//# sourceMappingURL=action-executor.js.map